import React, { useEffect, useState } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useDevice } from '../../hooks/useDevice';

const DISMISSED_KEY = 'push_prompt_dismissed';
const DISMISS_DELAY_DAYS = 7;

interface NotificationPromptProps {
  config: any;
  forceShow?: boolean;
  onClose?: () => void;
}

export const NotificationPrompt: React.FC<NotificationPromptProps> = ({
  config,
  forceShow = false,
  onClose,
}) => {
  const { status, isSupported, requestPermission } = usePushNotifications();
  const { isDesktop } = useDevice();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupported) return;
    if (status === 'granted' || status === 'denied') return;
    if (isDesktop && !forceShow) return;

    if (!forceShow) {
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        const daysElapsed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysElapsed < DISMISS_DELAY_DAYS) return;
      }
    }

    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [isSupported, status, forceShow, isDesktop]);

  const handleAccept = async () => {
    setLoading(true);
    await requestPermission();
    setLoading(false);
    setVisible(false);
    onClose?.();
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
    onClose?.();
  };

  if (!visible) return null;

  const majorColor = config?.['major-color'] || '#6B7280';
  const darkColor = config?.['dark-color'] || '#1F2937';
  const borderRadius = config?.border_radius || '0.5rem';

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[350] flex justify-center pointer-events-none">
      <div
        className="bg-white shadow-xl border border-gray-100 max-w-sm w-full pointer-events-auto overflow-hidden"
        style={{ borderRadius }}
      >
        <div className="flex items-start gap-3 p-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: majorColor }}
          >
            <Bell size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-snug" style={{ color: darkColor }}>
              Restez informé
            </p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">
              Recevez les alertes et informations de l'hôtel directement sur votre appareil.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAccept}
                disabled={loading}
                className="flex-1 py-1.5 px-3 text-white text-xs font-medium rounded-md transition-opacity"
                style={{ backgroundColor: majorColor, borderRadius: '0.375rem', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Activation...' : 'Activer'}
              </button>
              <button
                onClick={handleDismiss}
                className="flex-1 py-1.5 px-3 text-gray-600 text-xs font-medium rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
                style={{ borderRadius: '0.375rem' }}
              >
                Plus tard
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={14} className="text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};
